import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/button/Button'
import { AlertErrorCircle } from '@/assets/icons'
import { isHeldByAnotherUser } from '@/editing-leases/utils/is-held-by-another-user'
import { matterPaths } from '@/routing/paths'
import { useAppSelector } from '@/store/store-hooks'
import { useTimelineEditingLease } from '../hooks/useTimelineEditingLease'

export default function TimelineLeaseExpiredNotice({ matterId, canResume }: { matterId: string; canResume: boolean }) {
  const navigate = useNavigate()
  const lease = useTimelineEditingLease()
  const hasAddedFiles = useAppSelector((state) => {
    const staging = state.timelineAssetState.addFilesStaging
    return staging.matterId === matterId && (staging.stagedUserFileIds.length > 0 || staging.pendingUploadClientIds.length > 0)
  })
  const [openingFilesForMatter, setOpeningFilesForMatter] = useState<string | null>(null)

  useEffect(() => {
    if (!openingFilesForMatter) return
    if (openingFilesForMatter !== matterId) {
      setOpeningFilesForMatter(null)
      return
    }
    if (lease.pending) return
    setOpeningFilesForMatter(null)
    if (canResume && lease.canWrite) navigate(matterPaths.timeline.addFiles(matterId))
  }, [openingFilesForMatter, matterId, canResume, lease.pending, lease.canWrite, navigate])

  return (
    <div
      role="status"
      className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 pl-4 pr-2 py-3 rounded-md bg-yellow-50 ring-1 ring-inset ring-brand-neutral-200"
    >
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 text-xs font-medium leading-4">
        <span className="flex items-center gap-1 shrink-0 text-yellow-800">
          <span className="flex size-6 shrink-0 items-center justify-center" aria-hidden>
            <AlertErrorCircle className="size-5" />
          </span>
          <span>Your editing session expired.</span>
        </span>
        {hasAddedFiles && <span className="text-brand-neutral-950">Your added files are still waiting to be submitted.</span>}
      </div>
      <Button
        variant="plain"
        color="secondary"
        size="xs"
        className="self-start sm:self-auto shrink-0"
        disabled={!canResume || lease.initializing || lease.pending || isHeldByAnotherUser(lease.lease)}
        loading={openingFilesForMatter === matterId && lease.pending}
        onClick={() => {
          if (lease.canWrite) navigate(matterPaths.timeline.addFiles(matterId))
          else {
            setOpeningFilesForMatter(matterId)
            lease.startEditing()
          }
        }}
      >
        Resume
      </Button>
    </div>
  )
}
